import { useTheme } from "@/hooks/useTheme";
import { useMemo } from "react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { MONTHS } from "@/constants/charts";

interface ChartsSectionProps {
  selectedCourse: string;
  courseStatus: any;
  courseStatusLoading: boolean;
  monthlyData: any;
  isLoading: boolean;
  isError: boolean;
  user: any;
  departments: any[];
  departmentsLoading: boolean;
  departmentsError: any;
  departmentChartData: any;
}

const ChartsSection = ({
  selectedCourse,
  courseStatus,
  courseStatusLoading,
  monthlyData,
  isLoading,
  isError,
  user,
  departments,
  departmentsLoading,
  departmentsError,
  departmentChartData,
}: ChartsSectionProps) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const chartData = useMemo(() => {
    const completedCourses = MONTHS.map((month) => monthlyData?.[month] ?? 0);

    return {
      labels: MONTHS,
      datasets: [
        {
          label: t("analytics.completedCourses"),
          data: completedCourses,
          borderColor: "#005f56",
          backgroundColor: "rgba(0, 95, 86, 0.1)",
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [monthlyData]);

  const isDark = theme === "dark";

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          color: isDark ? "#9ca3af" : "#4b5563",
        },
      },
      x: {
        grid: {
          color: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          color: isDark ? "#9ca3af" : "#4b5563",
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          color: isDark ? "#e5e7eb" : "#1f2937",
        },
      },
      tooltip: {
        backgroundColor: isDark ? "#374151" : "#ffffff",
        titleColor: isDark ? "#e5e7eb" : "#1f2937",
        bodyColor: isDark ? "#9ca3af" : "#4b5563",
        borderColor: isDark ? "#4b5563" : "#e5e7eb",
        borderWidth: 1,
      },
    },
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
          display: true,
        },
        ticks: {
          color: isDark ? "#9ca3af" : "#4b5563",
        },
      },
      x: {
        grid: {
          color: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
          display: false,
        },
        ticks: {
          color: isDark ? "#9ca3af" : "#4b5563",
          maxRotation: 45,
          minRotation: 0,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDark ? "#374151" : "#ffffff",
        titleColor: isDark ? "#e5e7eb" : "#1f2937",
        bodyColor: isDark ? "#9ca3af" : "#4b5563",
        borderColor: isDark ? "#4b5563" : "#e5e7eb",
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: function(context: any) {
            return context[0].label;
          },
          label: function(context: any) {
            return `Score: ${context.parsed.y}`;
          },
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          color: isDark ? "#e5e7eb" : "#1f2937",
        },
      },
      tooltip: {
        backgroundColor: isDark ? "#374151" : "#ffffff",
        titleColor: isDark ? "#e5e7eb" : "#1f2937",
        bodyColor: isDark ? "#9ca3af" : "#4b5563",
        borderColor: isDark ? "#4b5563" : "#e5e7eb",
        borderWidth: 1,
      },
    },
  };

  const mockDoughnutChartData = {
    labels: [t("analytics.completed"), t("analytics.inProgress"), t("analytics.notStarted")],
    datasets: [
      {
        data: [55, 25, 20],
        backgroundColor: [
          "rgba(34, 197, 94, 0.8)",
          "rgba(59, 130, 246, 0.8)",
          "rgba(209, 213, 219, 0.8)",
        ],
        borderWidth: 1,
      },
    ],
  };

  let filteredDoughnutChartData = mockDoughnutChartData;
  if (selectedCourse && courseStatus) {
    filteredDoughnutChartData = {
      labels: [t("analytics.completed"), t("analytics.inProgress"), t("analytics.notStarted")],
      datasets: [
        {
          data: [
            courseStatus.completed,
            courseStatus.inProgress,
            courseStatus.notStarted,
          ],
          backgroundColor: [
            "rgba(34, 197, 94, 0.8)",
            "rgba(59, 130, 246, 0.8)",
            "rgba(209, 213, 219, 0.8)",
          ],
          borderWidth: 1,
        },
      ],
    };
  }

  return (
    <>
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 mb-8 px-2 sm:px-0">
        {/* Course Completion Chart */}
        <div className="bg-white/5 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200/20 dark:border-gray-700/50 shadow-xl overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-200/20 dark:border-gray-700/50">
            <h3 className="text-lg sm:text-xl font-bold text-white">
              {t("analytics.courseCompletion")}
            </h3>
            <p className="text-xs sm:text-sm text-gray-300 mt-1">
              {t("analytics.courseCompletionTrend")}
            </p>
          </div>
          <div className="p-4 sm:p-6 h-64 sm:h-80">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("analytics.loadingChartData")}
                  </p>
                </div>
              </div>
            ) : isError ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <svg
                    className="w-12 h-12 text-red-400 mx-auto mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <p className="text-red-500 dark:text-red-400 font-medium">
                    {t("analytics.errorLoadingData")}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t("analytics.pleaseTryAgainLater")}
                  </p>
                </div>
              </div>
            ) : (
              <Line data={chartData} options={lineOptions} />
            )}
          </div>
        </div>

        {/* Course Status Chart */}
        <div className="bg-white/5 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200/20 dark:border-gray-700/50 shadow-xl overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-200/20 dark:border-gray-700/50">
            <h3 className="text-lg sm:text-xl font-bold text-white">
              {t("analytics.courseStatus")}
            </h3>
            <p className="text-xs sm:text-sm text-gray-300 mt-1">
              {t("analytics.courseEnrollmentStatus")}
            </p>
          </div>
          <div className="p-4 sm:p-6 h-64 sm:h-80 flex items-center justify-center">
            {courseStatusLoading && selectedCourse ? (
              <div className="flex flex-col items-center space-y-3">
                <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("analytics.loadingStatus")}
                </p>
              </div>
            ) : selectedCourse &&
              (!courseStatus ||
                (courseStatus.completed === 0 &&
                  courseStatus.inProgress === 0 &&
                  courseStatus.notStarted === 0)) ? (
              <div className="text-center">
                <svg
                  className="w-12 h-12 text-gray-400 mx-auto mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-gray-500 dark:text-gray-400 font-medium">
                  {t("analytics.noDataAvailable")}
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  {t(
                    "analytics.noRegisteredUser",
                    "No courses started yet."
                  )}
                </p>
              </div>
            ) : (
              <Doughnut
                data={filteredDoughnutChartData}
                options={doughnutOptions}
              />
            )}
          </div>
        </div>
      </div>

      {/* Department Comparison - Show only for Company Admin */}
      {user?.role === "company_admin" && (
        <div className="mb-8 px-2 sm:px-0">
          <div className="bg-white/5 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200/20 dark:border-gray-700/50 shadow-xl overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-gray-200/20 dark:border-gray-700/50">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                {t("analytics.departmentComparison")}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t("analytics.departmentScores")}
              </p>
            </div>
            <div className="p-4 sm:p-6 h-64 sm:h-80">
              {departmentsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex flex-col items-center space-y-3">
                    <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t("analytics.loadingDepartmentData")}
                    </p>
                  </div>
                </div>
              ) : departmentsError ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <svg
                      className="w-12 h-12 text-red-400 mx-auto mb-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                    <p className="text-red-500 dark:text-red-400 font-medium">
                      {t("analytics.errorLoadingDepartmentData")}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {(departmentsError as any)?.message || t("analytics.pleaseTryAgainLater")}
                    </p>
                  </div>
                </div>
              ) : departmentChartData && departmentChartData.labels.length > 0 ? (
                <Bar data={departmentChartData} options={barOptions} />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <svg
                      className="w-12 h-12 text-gray-400 mx-auto mb-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                      {t("analytics.noDepartmentDataAvailable")}
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                      {t("analytics.noDepartmentsFound")}
                    </p>
                    <div className="mt-4 text-xs text-gray-500">
                      <p>Debug info:</p>
                      <p>User role: {user?.role}</p>
                      <p>Should fetch: {user?.role === "company_admin" ? 'Yes' : 'No'}</p>
                      <p>Data length: {departments?.length || 0}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChartsSection;
